import { useRef, useCallback, useEffect, useLayoutEffect, useState } from "react";
import { useNavigate } from "react-router";
import Select, {
  components,
  type ControlProps,
  type GroupBase,
  type MenuListProps,
  type Props as SelectProps,
  type SingleValue,
} from "react-select";
import AxiosInstance from "@/utils/AxiosInstance";
import { useOrg } from "@/contexts/useOrg";
import { Loader2, Search, X } from "lucide-react";

type PatientOption = { label: string; value: string };

type PatientSelectProps = SelectProps<PatientOption, false, GroupBase<PatientOption>> & {
  isLoadingMore?: boolean;
  menuScrollTopRef?: React.MutableRefObject<number>;
  shouldRestoreScrollRef?: React.MutableRefObject<boolean>;
};

const MIN_SEARCH_LENGTH = 3;
const DEBOUNCE_MS = 1000;
const PAGE_SIZE = 10;

const SearchControl = (props: ControlProps<PatientOption, false>) => (
  <components.Control {...props}>
    <span className="pl-2 text-muted-foreground">
      <Search className="h-4 w-4" />
    </span>
    {props.children}
  </components.Control>
);

function MenuListWithLoader(props: MenuListProps<PatientOption, false>) {
  const selectProps = props.selectProps as PatientSelectProps;
  const isLoadingMore = Boolean(selectProps.isLoadingMore);
  const menuScrollTopRef = selectProps.menuScrollTopRef;
  const shouldRestoreScrollRef = selectProps.shouldRestoreScrollRef;
  const listRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const el = listRef.current;
    if (!el || !shouldRestoreScrollRef?.current || !menuScrollTopRef) return;
    el.scrollTop = menuScrollTopRef.current;
    // Keep restoring across the options + loading-more state updates, then clear.
    if (!isLoadingMore) {
      shouldRestoreScrollRef.current = false;
    }
  });

  return (
    <components.MenuList
      {...props}
      innerRef={(node) => {
        listRef.current = node;
        const parentRef = props.innerRef;
        if (typeof parentRef === "function") {
          parentRef(node);
        } else if (parentRef) {
          (parentRef as React.MutableRefObject<HTMLDivElement | null>).current =
            node;
        }
      }}
    >
      {props.children}
      {isLoadingMore ? (
        <div className="flex items-center justify-center gap-2 py-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading more...
        </div>
      ) : null}
    </components.MenuList>
  );
}

const selectComponents = {
  Control: SearchControl,
  MenuList: MenuListWithLoader,
};

function mapPatientsToOptions(patients: any[]): PatientOption[] {
  return (patients || []).map((p: any) => ({
    label: `${p.patientId || ""} - ${p.firstName || ""} ${p.middleName || ""} ${p.lastName || ""}`.trim(),
    value: p._id,
  }));
}

export default function GlobalPatientSearch() {
  const navigate = useNavigate();
  const { selectedOrgId } = useOrg();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);
  const pageRef = useRef(1);
  const totalRef = useRef(0);
  const searchRef = useRef("");
  const loadingMoreRef = useRef(false);
  const menuScrollTopRef = useRef(0);
  const shouldRestoreScrollRef = useRef(false);

  const [inputValue, setInputValue] = useState("");
  const [value, setValue] = useState<PatientOption | null>(null);
  const [options, setOptions] = useState<PatientOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  const buildParams = useCallback(
    (search: string, page: number) => ({
      search,
      page,
      limit: PAGE_SIZE,
      skip: (page - 1) * PAGE_SIZE,
      ...(selectedOrgId && selectedOrgId !== "anthro-care"
        ? { organizationId: selectedOrgId }
        : {}),
    }),
    [selectedOrgId],
  );

  const fetchPage = useCallback(
    async (search: string, page: number) => {
      const response: any = await AxiosInstance().get("/patients", {
        params: buildParams(search, page),
      });
      const patients = response?.data?.data?.patients || [];
      const total = Number(response?.data?.data?.total ?? 0);
      return {
        options: mapPatientsToOptions(patients),
        total,
      };
    },
    [buildParams],
  );

  // Debounced search — always starts from page 1
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    const trimmed = inputValue.trim();
    if (trimmed.length < MIN_SEARCH_LENGTH) {
      requestIdRef.current += 1;
      setOptions([]);
      setHasMore(false);
      setIsLoading(false);
      setIsLoadingMore(false);
      pageRef.current = 1;
      totalRef.current = 0;
      searchRef.current = "";
      loadingMoreRef.current = false;
      shouldRestoreScrollRef.current = false;
      menuScrollTopRef.current = 0;
      return;
    }

    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    shouldRestoreScrollRef.current = false;
    menuScrollTopRef.current = 0;

    timerRef.current = setTimeout(async () => {
      try {
        const { options: nextOptions, total } = await fetchPage(trimmed, 1);
        if (requestId !== requestIdRef.current) return;

        searchRef.current = trimmed;
        pageRef.current = 1;
        totalRef.current = total;
        setOptions(nextOptions);
        setHasMore(nextOptions.length < total);
      } catch {
        if (requestId !== requestIdRef.current) return;
        setOptions([]);
        setHasMore(false);
      } finally {
        if (requestId === requestIdRef.current) {
          setIsLoading(false);
        }
      }
    }, DEBOUNCE_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [inputValue, fetchPage]);

  const handleMenuScrollToBottom = useCallback(async () => {
    if (
      !hasMore ||
      isLoading ||
      isLoadingMore ||
      loadingMoreRef.current ||
      !searchRef.current
    ) {
      return;
    }

    loadingMoreRef.current = true;
    setIsLoadingMore(true);
    const nextPage = pageRef.current + 1;
    const requestId = requestIdRef.current;

    // Keep the menu at the same scroll position after options are appended.
    const menuList = document.querySelector(
      ".global-patient-search__menu-list",
    ) as HTMLDivElement | null;
    if (menuList) {
      menuScrollTopRef.current = menuList.scrollTop;
    }
    shouldRestoreScrollRef.current = true;

    try {
      const { options: moreOptions, total } = await fetchPage(
        searchRef.current,
        nextPage,
      );
      if (requestId !== requestIdRef.current) {
        loadingMoreRef.current = false;
        shouldRestoreScrollRef.current = false;
        return;
      }

      const latestMenuList = document.querySelector(
        ".global-patient-search__menu-list",
      ) as HTMLDivElement | null;
      if (latestMenuList) {
        menuScrollTopRef.current = latestMenuList.scrollTop;
      }
      shouldRestoreScrollRef.current = true;

      pageRef.current = nextPage;
      totalRef.current = total;
      setOptions((prev) => {
        const existing = new Set(prev.map((o) => o.value));
        const appended = moreOptions.filter((o) => !existing.has(o.value));
        const merged = [...prev, ...appended];
        setHasMore(merged.length < total);
        return merged;
      });
      setIsLoadingMore(false);
      loadingMoreRef.current = false;
    } catch {
      shouldRestoreScrollRef.current = false;
      loadingMoreRef.current = false;
      if (requestId === requestIdRef.current) {
        setIsLoadingMore(false);
      }
    }
  }, [fetchPage, hasMore, isLoading, isLoadingMore]);

  const handleSelect = (option: SingleValue<PatientOption>) => {
    if (option?.value) {
      navigate(`/patients/${option.value}`);
      setValue(null);
      setInputValue("");
      setOptions([]);
      setHasMore(false);
      pageRef.current = 1;
      totalRef.current = 0;
      searchRef.current = "";
      shouldRestoreScrollRef.current = false;
      menuScrollTopRef.current = 0;
    }
  };

  const clearSearch = () => {
    requestIdRef.current += 1;
    setValue(null);
    setInputValue("");
    setOptions([]);
    setHasMore(false);
    setIsLoading(false);
    setIsLoadingMore(false);
    pageRef.current = 1;
    totalRef.current = 0;
    searchRef.current = "";
    loadingMoreRef.current = false;
    shouldRestoreScrollRef.current = false;
    menuScrollTopRef.current = 0;
  };

  return (
    <div className="hidden md:block w-full max-w-sm">
      <div className="relative">
        <Select<PatientOption, false>
          classNamePrefix="global-patient-search"
          components={selectComponents}
          options={options}
          onChange={(option) => {
            setValue(option ?? null);
            handleSelect(option);
          }}
          value={value}
          inputValue={inputValue}
          onInputChange={(next, meta) => {
            if (meta.action === "input-change") setInputValue(next);
            return next;
          }}
          onMenuScrollToBottom={handleMenuScrollToBottom}
          filterOption={null}
          isLoading={isLoading}
          // Custom props consumed by MenuList via selectProps
          {...({
            isLoadingMore,
            menuScrollTopRef,
            shouldRestoreScrollRef,
          } as Partial<PatientSelectProps>)}
          placeholder="Search patients by name, email, ID or number"
          isClearable
          noOptionsMessage={({ inputValue: iv }) =>
            iv.trim().length < MIN_SEARCH_LENGTH
              ? `Type at least ${MIN_SEARCH_LENGTH} characters to search`
              : isLoading
                ? "Searching..."
                : "No patients found"
          }
          loadingMessage={() => "Searching..."}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 99999 }),
            control: (base) => ({
              ...base,
              minHeight: 36,
              fontSize: 14,
              paddingLeft: 4,
              textOverflow: "ellipsis !important",
              whiteSpace: "nowrap !important",
            }),
            placeholder: (base) => ({ ...base, fontSize: 14 }),
            input: (base) => ({ ...base, fontSize: 14 }),
          }}
          menuPortalTarget={document.body}
          menuPosition="fixed"
        />
        {inputValue !== "" && !isLoading && (
          <button
            type="button"
            onClick={clearSearch}
            aria-label="Clear search"
            className="absolute right-12 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}
